/**
 * Acciones de Emails (`/activa/emails/acciones`): qué hacer con lo que dicen los mails del período. Pedido de Alan del
 * 12/10/2026: «una página de acciones a realizar… dato → información → acción, que explique qué acciones deberíamos
 * realizar».
 *
 * - Cada acción en tres pasos: el dato medido, lo que quiere decir y qué hacer (con el link a la lista que hay que mirar).
 *   Las reglas y sus bases mínimas están en `logic/acciones.ts`.
 * - Sale del resumen y del análisis a fondo del mismo rango. Si el análisis falla, se muestran las del resumen y se dice
 *   cuáles faltan.
 * - Lo que se hace en la cuenta de Snov (la de Deenex) sólo lo ve el equipo.
 * - El rango vive en la URL, como en el Resumen.
 */

import { useMemo, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import { emailsApi } from "../api";
import type { AnalisisEmails, ResumenEmails } from "../types";
import { usePedido } from "../hooks/usePedido";
import { useEstadoEmails } from "../contexto";
import { Esqueleto, ErrorTarjeta } from "../../wa-oficial/components/estadisticas/comun";
import { ErrorRefresco } from "../components/ErrorRefresco";
import { SelectorRango } from "../../wa-oficial/components/estadisticas/SelectorRango";
import type { ClaveRango } from "../../wa-oficial/logic/estadisticas";
import { rangoDesdeParams } from "../../wa-oficial/logic/estadisticas";
import type { RangoFechas } from "../../wa-oficial/types.estadisticas";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { num, plural } from "../../campana-nueva/utils/format";
import { accionesEmails, type AccionEmails, type Prioridad } from "../logic/acciones";
import { R_EMAILS } from "../logic/emails";

const PRIORIDAD: Record<Prioridad, { texto: string; cls: string }> = {
  alta: { texto: "Urgente", cls: "bg-rose-50 text-rose-700" },
  media: { texto: "Importante", cls: "bg-amber-50 text-amber-800" },
  baja: { texto: "Para mejorar", cls: "bg-slate-100 text-slate-600" },
};

function Paso({ etiqueta, cls, children }: { etiqueta: string; cls: string; children: ReactNode }) {
  return (
    <div className={`rounded-xl px-3 py-2.5 ${cls}`}>
      <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">{etiqueta}</p>
      <div className="mt-1 text-sm leading-relaxed">{children}</div>
    </div>
  );
}

const Flecha = () => (
  <div className="flex items-center justify-center text-slate-300" aria-hidden="true">
    <ArrowRight size={16} className="rotate-90 md:rotate-0" />
  </div>
);

export function TarjetaAccion({ a, equipo }: { a: AccionEmails; equipo: boolean }) {
  const p = PRIORIDAD[a.prioridad];
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" aria-labelledby={`accion-${a.id}`} data-testid={`accion-${a.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${p.cls}`}>{p.texto}</span>
        <h3 id={`accion-${a.id}`} className="text-base font-semibold text-slate-900">{a.titulo}</h3>
      </div>
      <div className="grid gap-2 md:grid-cols-[1fr_auto_1fr_auto_1fr]">
        <Paso etiqueta="Dato" cls="bg-slate-50 text-slate-800">{a.dato}</Paso>
        <Flecha />
        <Paso etiqueta="Información" cls="bg-sky-50 text-sky-900">{a.informacion}</Paso>
        <Flecha />
        <Paso etiqueta="Acción" cls="bg-violet-50 text-violet-900">
          <p className="font-medium">{a.accion}</p>
          {a.enlace && (
            <Link to={a.enlace.a} className="mt-1.5 inline-block text-sm font-semibold text-violet-700 underline">{a.enlace.texto}</Link>
          )}
          {equipo && a.equipo && <p className="mt-1.5 text-xs text-violet-800/80">En Snov (equipo): {a.equipo}</p>}
        </Paso>
      </div>
    </article>
  );
}

export default function AccionesEmailsPage() {
  const [sp, setSp] = useSearchParams();
  const spTexto = sp.toString();
  const { rango, clave, error: errorUrl } = useMemo(() => rangoDesdeParams(new URLSearchParams(spTexto), new Date()), [spTexto]);
  const { version } = useEstadoEmails();
  const equipo = useEsEquipoDeenex();
  const claveRango = `${rango.desde}|${rango.hasta}`;
  const r = usePedido<ResumenEmails>(() => emailsApi.resumen(rango), claveRango, { refresco: version });
  const an = usePedido<AnalisisEmails>(() => emailsApi.analisis(rango), claveRango, { refresco: version });
  const acciones = useMemo(() => (r.dato ? accionesEmails(r.dato, an.dato) : []), [r.dato, an.dato]);

  const elegir = (c: ClaveRango) => setSp(new URLSearchParams({ rango: c }), { replace: true });
  const personalizado = (x: RangoFechas) => setSp(new URLSearchParams({ desde: x.desde, hasta: x.hasta }), { replace: true });
  const d = r.dato;
  const urgentes = acciones.filter((a) => a.prioridad === "alta").length;

  return (
    <div className="space-y-6">
      <header className="space-y-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight">Qué hacer con lo que dicen los mails</h2>
          <p className="text-sm text-slate-500 mt-1">
            Cada acción sale de algo medido en estas fechas: el <strong className="font-semibold text-slate-700">dato</strong>, lo que
            {" "}<strong className="font-semibold text-slate-700">quiere decir</strong> y <strong className="font-semibold text-slate-700">qué hacer</strong>.
            Sólo aparece cuando hay suficientes mails para afirmarlo.
          </p>
        </div>
        <SelectorRango clave={clave} rango={rango} onElegir={elegir} onPersonalizado={personalizado} />
        {errorUrl && <p className="text-xs text-amber-700" role="status">{errorUrl}</p>}
        {d && r.error && <ErrorRefresco error={r.error} onReintentar={r.recargar} />}
      </header>

      {!d ? (
        r.error ? <ErrorTarjeta texto={`No pudimos traer los datos. ${r.error}`} onReintentar={r.recargar} /> : <Esqueleto alto="h-64" etiqueta="Cargando las acciones" />
      ) : d.totalCampanas === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
          <Mail size={22} className="mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-600">Todavía no hay campañas de mail de este comercio: sin mails no hay nada que medir.</p>
        </div>
      ) : (
        <>
          {an.error && !an.dato && (
            <div className="rounded-xl border border-amber-100 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status">
              No pudimos traer el análisis a fondo: faltan las acciones que salen de él (proveedores de correo, casillas, interesados y
              horarios). <button type="button" onClick={an.recargar} className="font-semibold underline">Reintentar</button>
            </div>
          )}
          {an.cargando && !an.dato && <p className="text-xs text-slate-500">Cargando el análisis a fondo: pueden aparecer más acciones.</p>}
          {acciones.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center" data-testid="sin-acciones">
              <p className="text-sm text-slate-600">Con lo medido en estas fechas no hay nada que corregir.</p>
              <p className="mt-1 text-xs text-slate-500">
                {d.conteos.campanas === 0 ? "No salió ninguna campaña en estos días: probá con un rango más largo." : "Con más mails enviados aparecen más cosas para mirar."}
              </p>
            </div>
          ) : (
            <section aria-label="Acciones" className="space-y-3">
              <p className="text-sm text-slate-600" data-testid="resumen-acciones">
                {num(acciones.length)} {plural(acciones.length, "acción", "acciones")}
                {urgentes > 0 ? `, ${num(urgentes)} ${plural(urgentes, "urgente", "urgentes")}` : ""}.
              </p>
              {acciones.map((a) => <TarjetaAccion key={a.id} a={a} equipo={equipo} />)}
            </section>
          )}
          <p className="text-xs text-slate-500">
            Los números de cada acción están en el <Link to={R_EMAILS} className="font-semibold text-violet-700 underline">Resumen</Link>, con el
            análisis a fondo abajo de todo.
          </p>
        </>
      )}
    </div>
  );
}
